import { ApiUser } from '@vend/types'
import {
  OpenUserSwitchingArguments,
  fetchApi,
  openUserSwitch,
  saveToStorage,
  subscribeToUserSwitchEvents,
  trackError,
} from '@vend/utilities'
import { VdActiveUser, VdFeatureManager } from '@vend/vend.js'
import { VdToastNotificationService } from 'vend.ui'
import { RegisterManager } from '../../../managers/register-manager'
import { UserManager } from '../../../managers/user-manager'

import {
  FEATURE_ENABLE_CONTROL_ON_DISCOUNT,
  PERMISSIONS,
  RULES,
} from '../../../constants'
import { WebRegisterStatusManager } from '../../../managers/web-register-status-manager'
import { $q } from '../../../ng-compat/imports'

const USI_PREFIX = 'usi:'
const FORCE_USER_SWITCH = 'force_user_switch'

/**
 * @class UserSwitchingService
 */
export class UserSwitchingService {
  public usiMatcher = new RegExp(`^${USI_PREFIX}([0-9A-Za-z]){12}$`)

  private ignoreNextUserSwitchEventFromChannel: boolean = false

  constructor(
    private registerManager: RegisterManager,
    private userManager: UserManager,
    private vdActiveUser: VdActiveUser,
    private webRegisterStatusManager: WebRegisterStatusManager,
    private vdToastNotificationService: VdToastNotificationService,
    private vdFeatureManager: VdFeatureManager
  ) {
    'ngInject'
    if (typeof BroadcastChannel !== 'undefined') {
      subscribeToUserSwitchEvents(this.handleUserSwitchEventsChannel.bind(this))
    }
  }

  /**
   * Open the "user switching" modal that prompts the user to choose a user to login as.
   *
   * @method selectUser
   *
   * @param  {Object} options
   *
   *         @param {User} [options.user]
   *                Can be passed if a chosen user needs to enter a password.
   *
   *         @param {Boolean} [options.selectUserForNextSale]
   *                Whether the context is selecting a user for next sale.
   *
   * @return {Promise} A promise resolved once the user has been switched or the modal cancelled.
   */
  public async selectUser(
    options: {
      selectUserForDiscount?: boolean
      selectUserForNextSale?: {
        shouldTaintSession: boolean
      }
    } = {}
  ): Promise<void> {
    const { selectUserForNextSale, selectUserForDiscount } = options

    this.ignoreNextUserSwitchEventFromChannel = true

    const outletId = this.registerManager.getActiveOutlet().id

    let openArgs: OpenUserSwitchingArguments = { filters: { outletId } }

    if (selectUserForDiscount) {
      const hasRulesEnabled = this.vdFeatureManager.get(
        FEATURE_ENABLE_CONTROL_ON_DISCOUNT
      )

      openArgs = {
        ...openArgs,
        filters: {
          ...openArgs.filters,
          permission: !hasRulesEnabled ? PERMISSIONS.SALE_DISCOUNT : undefined,
          rule: hasRulesEnabled ? RULES.TOTAL_SALE_DISCOUNT : undefined,
        },
        title: 'Permission required to apply a discount',
        body:
          'You don’t have permission to apply discounts to a sale. Please switch to another user to proceed.',
        userListHeader: 'All users who have permission to apply a discount',
      }
    } else if (selectUserForNextSale) {
      // Refresh the register status to ensure we're online before forcing the switch.
      await this.webRegisterStatusManager.refreshPosStatus()
      // If we're offline, don't open the user switch modal to force a switch
      // as the user would be stuck in a non-dismissible modal.
      if (!this.webRegisterStatusManager.isOnline()) {
        return
      }

      saveToStorage(FORCE_USER_SWITCH, 'true')

      if (selectUserForNextSale.shouldTaintSession) {
        this.taintSessionToForceSwitch()
      }

      openArgs = {
        ...openArgs,
        title: 'Choose a user to start the next sale',
        blocking: true,
      }
    }

    // open user switch will return a user if we did switch
    // or undefined if we didn't. Either way we still want to resolve
    // to match the behaviour of the modal closing with no changes
    const user = await openUserSwitch(openArgs)
    if (!user) {
      return
    }

    return new Promise<void>((resolve, reject) => {
      this.handleNewSwitchSuccessCallback(user)
        .then(() => resolve())
        .catch(reason => reject(reason))
    })
  }

  /**
   * Trigger a blocking switch of user if register settings requires
   *
   * @method switchUserIfNeededOnCompleteSale
   *
   */
  public switchUserIfNeededOnCompleteSale(): Promise<void> {
    return this.switchUserIfNeeded(true)
  }

  /**
   * Trigger a switch of user if register settings requires
   *
   * @method switchUserIfNeeded
   */
  public switchUserIfNeeded(
    isOnSaleConfirmation: boolean = false
  ): Promise<void> {
    const register = this.registerManager.getActiveRegister()
    const askForUserOnSale = register && register.ask_for_user_on_sale

    if (askForUserOnSale) {
      return this.selectUser({
        selectUserForNextSale: {
          shouldTaintSession: isOnSaleConfirmation,
        },
      })
    }

    return Promise.resolve()
  }

  /**
   * Identifies a User Switch Id (usi).
   *
   * @method isUserSwitchId
   *
   * @param {String} query
   *        The query to evaluate.
   *
   * @return {Boolean} True if the provided query is a usi, false otherwise.
   */
  public isUserSwitchId(query: string) {
    return this.usiMatcher.test(query)
  }

  /**
   * Attempts to fast switch to another user based off the supplied User Switch id.
   *
   * @method fastSwitch
   *
   * @param  {Object} params
   *
   *         @param {String} params.switchId
   *                The User Switch Id
   *
   * @return {Promise<User>} Resolved once a successful user switch has occurred.
   */
  public fastSwitch({ switchId }: { switchId: string }) {
    return $q((resolve, reject) => {
      if (!this.webRegisterStatusManager.isOnline()) {
        this.vdToastNotificationService.negative({
          message: 'You’re currently offline and are unable to switch users.',
        })
        return reject()
      }

      openUserSwitch({ switchId }).then(user => {
        if (user) {
          this.handleNewSwitchSuccessCallback(user).then(() => resolve(user))
        } else {
          reject()
        }
      })
    })
  }

  // The navigation package will always emit a broadcast channel message
  // when a user is switched. We want to ignore that message when the webregister
  // code initiates that switch, otherwise we will switch twice.
  private handleUserSwitchEventsChannel(user: ApiUser) {
    if (this.ignoreNextUserSwitchEventFromChannel) {
      this.ignoreNextUserSwitchEventFromChannel = false
      return
    }
    this.vdActiveUser.reload().then(() => {
      this.userManager.setLoggedInUser(user)
    })
  }

  // handleNewSwitchSuccessCallback handles the callback from our new
  // monocle-navigation based user switching.
  private handleNewSwitchSuccessCallback(user: ApiUser) {
    // vdActiveUser contains it's own internal copy of which user is currently
    // signed in. We need to refresh that before calling the webregister's
    // setLoggedInUser function to replicate how switching used to work.
    // Ideally, at some point, we should rip out this state.
    return $q(resolve => {
      this.vdActiveUser.reload().then(() => {
        this.userManager.setLoggedInUser(user)
        resolve()
      })
    })
  }

  private async taintSessionToForceSwitch() {
    const response = await fetchApi(`/api/session/taint`, {
      method: 'POST',
      body: JSON.stringify({
        type: 'force_switch',
      }),
    })

    if (!response.ok) {
      trackError('Failed to taint session to force user switch')
    }
  }
}
